export type PlacementType = 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'

export interface FitCheckParams {
  buttonRect: DOMRect
  popoverRect: DOMRect
  margin: number
  viewportWidth: number
  viewportHeight: number
}

/**
 * A popover can sit beside a large container while staying aligned with a
 * control inside it. When neither side fits, use that control's track as the
 * vertical fallback anchor instead of the whole container.
 */
export function findEffectivePlacementWithFallback(
  placement: PlacementType,
  params: FitCheckParams,
  fallbackRect?: DOMRect,
): { placement: PlacementType; anchorRect: DOMRect } {
  const { buttonRect, popoverRect, margin, viewportWidth } = params
  if (fallbackRect && (placement === 'left' || placement === 'right')) {
    const fitsRight = buttonRect.right + margin + popoverRect.width <= viewportWidth
    const fitsLeft = buttonRect.left - margin - popoverRect.width >= 0
    if (!fitsRight && !fitsLeft) {
      return {
        placement: fallbackRect.top - margin - popoverRect.height >= 0 ? 'topLeft' : 'bottomLeft',
        anchorRect: fallbackRect,
      }
    }
  }

  return { placement: findEffectivePlacement(placement, params), anchorRect: buttonRect }
}

/**
 * Determines the effective placement for the popover based on available space.
 * Auto-flips to alternative positions when there's not enough space in the preferred direction.
 * 
 * Priority order for vertical placements (bottom/top):
 *   bottom → top → right → left → clamp to viewport
 * 
 * Priority order for horizontal placements (left/right):
 *   left/right → opposite side → top → bottom → clamp to viewport
 */
export function findEffectivePlacement(
  placement: PlacementType,
  { buttonRect, popoverRect, margin, viewportWidth, viewportHeight }: FitCheckParams
): PlacementType {
  // Helper functions to check if position fits within viewport
  const fitsBelow = () => buttonRect.bottom + margin + popoverRect.height <= viewportHeight
  const fitsAbove = () => buttonRect.top - margin - popoverRect.height >= 0
  const fitsRight = () => buttonRect.right + margin + popoverRect.width <= viewportWidth
  const fitsLeft = () => buttonRect.left - margin - popoverRect.width >= 0

  const isVerticalPlacement = placement === 'bottom' || placement === 'bottomLeft' || placement === 'bottomRight' ||
                               placement === 'top' || placement === 'topLeft' || placement === 'topRight'
  const isHorizontalPlacement = placement === 'left' || placement === 'right'

  let effectivePlacement: PlacementType = placement

  // Auto-flip for vertical placements (bottom/top variants)
  if (isVerticalPlacement) {
    const canFitBelow = fitsBelow()
    const canFitAbove = fitsAbove()

    if (placement === 'bottom' || placement === 'bottomLeft' || placement === 'bottomRight') {
      if (!canFitBelow && canFitAbove) {
        // Flip to top variant
        effectivePlacement = placement === 'bottom' ? 'top' : 
                            placement === 'bottomLeft' ? 'topLeft' : 'topRight'
      } else if (!canFitBelow && !canFitAbove) {
        // Neither top nor bottom fits - try horizontal placement (right first, then left)
        if (fitsRight()) {
          effectivePlacement = 'right'
        } else if (fitsLeft()) {
          effectivePlacement = 'left'
        }
        // If neither horizontal fits, fall back to boundary clamping
      }
    } else if (placement === 'top' || placement === 'topLeft' || placement === 'topRight') {
      if (!canFitAbove && canFitBelow) {
        // Flip to bottom variant
        effectivePlacement = placement === 'top' ? 'bottom' : 
                            placement === 'topLeft' ? 'bottomLeft' : 'bottomRight'
      } else if (!canFitAbove && !canFitBelow) {
        // Neither top nor bottom fits - try horizontal placement (right first, then left)
        if (fitsRight()) {
          effectivePlacement = 'right'
        } else if (fitsLeft()) {
          effectivePlacement = 'left'
        }
        // If neither horizontal fits, fall back to boundary clamping
      }
    }
  }

  // Auto-flip for horizontal placements (left/right)
  if (isHorizontalPlacement) {
    const canFitRight = fitsRight()
    const canFitLeft = fitsLeft()

    if (placement === 'right') {
      if (!canFitRight && canFitLeft) {
        effectivePlacement = 'left'
      } else if (!canFitRight && !canFitLeft) {
        // Neither left nor right fits - try vertical placement (top first, then bottom)
        if (fitsAbove()) {
          effectivePlacement = 'top'
        } else if (fitsBelow()) {
          effectivePlacement = 'bottom'
        }
      }
    } else if (placement === 'left') {
      if (!canFitLeft && canFitRight) {
        effectivePlacement = 'right'
      } else if (!canFitLeft && !canFitRight) {
        // Neither left nor right fits - try vertical placement (top first, then bottom)
        if (fitsAbove()) {
          effectivePlacement = 'top'
        } else if (fitsBelow()) {
          effectivePlacement = 'bottom'
        }
      }
    }
  }

  return effectivePlacement
}
